import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WebIcon from '@mui/icons-material/Web';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { ReferralWithMetadata } from '../../types/business';

interface ReferralsTableProps {
  referrals: ReferralWithMetadata[];
  isLoading: boolean;
  error: string | null;
  onEditReferral: (referral: ReferralWithMetadata) => void;
  onDeleteReferral: (referralId: string) => Promise<{ success: boolean; error?: string }>;
  isDeleting: boolean;
  userRole: 'admin' | 'agent';
  currentUserEmail?: string;
}

const ReferralActionsDropdown: React.FC<{
  referral: ReferralWithMetadata;
  onEdit: (referral: ReferralWithMetadata) => void;
  onDelete: (referralId: string) => Promise<{ success: boolean; error?: string }>;
  isDeleting: boolean;
  userRole: 'admin' | 'agent';
  currentUserEmail?: string;
}> = ({ referral, onEdit, onDelete, isDeleting, userRole, currentUserEmail }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    onEdit(referral);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    const result = await onDelete(referral.id);
    
    if (result.success) {
      setDeleteDialogOpen(false);
    } else {
      setError(result.error || 'Failed to delete referral');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setError(null);
  };

  const canEdit = referral.isEditable;
  const canDelete = userRole === 'admin' || 
    (referral.addedBy.type === 'agent' && referral.addedBy.email === currentUserEmail);

  if (!canEdit && !canDelete) {
    return null;
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={isDeleting}
      >
        <MoreVertIcon />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canEdit && (
          <MenuItem onClick={handleEditClick}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Referral</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete the referral for <strong>{referral.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const getSolutionCategoryColor = (category: string) => {
  switch (category) {
    case 'electrician': return 'warning';
    case 'plumber': return 'info';
    case 'hvac': return 'success';
    case 'handyman': return 'primary';
    default: return 'default';
  }
};

const getSolutionCategoryIcon = (category: string) => {
  switch (category) {
    case 'electrician': return '⚡';
    case 'plumber': return '🔧';
    case 'hvac': return '🌡️';
    case 'handyman': return '🔨';
    default: return '🏠';
  }
};

const ReferralsTable: React.FC<ReferralsTableProps> = ({
  referrals,
  isLoading,
  error,
  onEditReferral,
  onDeleteReferral,
  isDeleting,
  userRole,
  currentUserEmail
}) => {
  const handleContactClick = (type: 'phone' | 'email' | 'website', value: string) => {
    if (type === 'phone') {
      window.open(`tel:${value}`);
    } else if (type === 'email') {
      window.open(`mailto:${value}`);
    } else if (type === 'website') {
      const url = value.startsWith('http') ? value : `https://${value}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: 1, 
        borderColor: 'divider' 
      }}>
        <Typography variant="h6">
          Professional Referrals ({isLoading ? '...' : referrals.length})
        </Typography>
      </Box>

      {error && (
        <Box sx={{ p: 2, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      {/* Loading State */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading referrals...
          </Typography>
        </Box>
      ) : referrals.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No referrals found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start building your professional network by adding trusted contractors and service providers.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Professional</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Specialties</TableCell>
                <TableCell>Added By</TableCell>
                <TableCell>Date Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals.map((referral) => (
                <TableRow key={referral.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {referral.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {referral.name}
                        </Typography>
                        {referral.company && (
                          <Typography variant="body2" color="text.secondary">
                            {referral.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{getSolutionCategoryIcon(referral.solutionCategory)}</span>
                          <span style={{ textTransform: 'capitalize' }}>
                            {referral.solutionCategory}
                          </span>
                        </Box>
                      }
                      color={getSolutionCategoryColor(referral.solutionCategory) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {referral.phone && (
                        <Tooltip title={`Call ${referral.phone}`}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleContactClick('phone', referral.phone!)}
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {referral.email && (
                        <Tooltip title={`Email ${referral.email}`}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleContactClick('email', referral.email!)}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {referral.website && (
                        <Tooltip title={`Visit ${referral.website}`}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleContactClick('website', referral.website!)}
                          >
                            <WebIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 200 }}>
                      {referral.specialties.slice(0, 2).map((specialty, index) => (
                        <Chip 
                          key={index}
                          label={specialty}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      ))}
                      {referral.specialties.length > 2 && (
                        <Tooltip title={referral.specialties.slice(2).join(', ')}>
                          <Chip 
                            label={`+${referral.specialties.length - 2} more`}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {referral.addedBy.type === 'business' ? (
                        <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
                      ) : (
                        <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'info.main' }} />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {referral.addedBy.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {referral.addedBy.type === 'business' ? 'Business' : 'Agent'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </Typography>
                    {referral.createdAt !== referral.updatedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Updated {new Date(referral.updatedAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <ReferralActionsDropdown 
                      referral={referral} 
                      onEdit={onEditReferral}
                      onDelete={onDeleteReferral}
                      isDeleting={isDeleting}
                      userRole={userRole}
                      currentUserEmail={currentUserEmail}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ReferralsTable;